import React, {useContext, useEffect, useState} from "react";
import {Outlet, useNavigate} from "react-router-dom";
import {
    Alert,
    Button, Form,
    FormFeedback,
    FormGroup,
    FormText,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader
} from "reactstrap";
import Navbar from "../components/Navbar";
import SideNav from "../components/SideNav";
import Breadcrumbs from "../components/BreadCrumbs";
import AuthContext from "../context/AuthContext";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    addMerchantKYCIndia,
    getAddonProductSettingsInfo,
    getCustomerSettingsInfo,
    getInvoiceSettingsInfo,
    getOrderSettingsInfo,
    getProductGroupSettingsInfo,
    getProductSettingsInfo,
    getStorefrontSettingsInfo,
    getState,
    sendPhoneOTP,
    verifyPhoneOTP
} from "../api/api";
import {toast} from "react-toastify";
import handleUnauthorized401MutateError from "../utils/handleUnauthorized401MutateError";
import Loader from "../components/Loader";
import Select from "react-select";
import handleUnauthorized401QueryError from "../utils/handleUnauthorized401QueryError";

const Layout = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const {refreshToken} = useContext(AuthContext)
    const currentYear = new Date().getFullYear();
    const [profilePic, setProfilePic] = useState("")
    const [gender, setGender] = useState("MALE")
    const [phone, setPhone] = useState("")
    const [OTP, setOTP] = useState("")

    useEffect(() => {
        if (user?.phone) setPhone(user.phone)
    }, [user?.phone])

    const [OTPSent, setOTPSent] = useState(false)
    const [identityProofType, setIdentityProofType] = useState("AADHAAR")
    const [identityProofNumber, setIdentityProofNumber] = useState("")
    const [identityProofFile, setIdentityProofFile] = useState(null)
    const [businessName, setBusinessName] = useState("")
    const [brandName, setBrandName] = useState("")
    const [brandLogo, setBrandLogo] = useState("")
    const [businessAddress1, setBusinessAddress1] = useState("")
    const [businessAddress2, setBusinessAddress2] = useState("")
    const [businessAddress3, setBusinessAddress3] = useState('')
    // const [businessCountry, setBusinessCountry] = useState("INDIA")
    const [businessState, setBusinessState] = useState("")
    const [businessCity, setBusinessCity] = useState("");
    const [businessPincode, setBusinessPincode] = useState("");
    const [businessProofType, setBusinessProofType] = useState("GSTIN");
    const [businessProofNumber, setBusinessProofNumber] = useState("");
    const [businessProofFile, setBusinessProofFile] = useState(null);
    const [GSTIN, setGSTIN] = useState("");
    const [GSTINError, setGSTINError] = useState("");
    const [CIN, setCIN] = useState("");
    const [PAN, setPAN] = useState("");
    const [gstRegistrationType, setGstRegistrationType] = useState("UNREGISTERED");

    const [stateOptions, setStateOptions] = useState([]);


    const [merchantKYCIndiaModal, setMerchantKYCIndiaModal] = useState(false);
    const toggleMerchantKYCIndiaModal = () => setMerchantKYCIndiaModal(!merchantKYCIndiaModal);

    const [phoneNumberVerifyModal, setPhoneNumberVerifyModal] = useState(false);
    const togglePhoneNumberVerifyModal = () => setPhoneNumberVerifyModal(!phoneNumberVerifyModal);

    const queryClient = useQueryClient()

    const productSettingsQuery = useQuery({
        queryKey: ['product-settings'],
        queryFn: () => getProductSettingsInfo(),
        enabled: !!user,
        retry: false,
        refetchOnWindowFocus: false,
        onError: (error) => {
            handleUnauthorized401QueryError(error, refreshToken, productSettingsQuery.refetch);
        }
    });

    const orderSettingsQuery = useQuery({
        queryKey: ['order-settings'],
        queryFn: () => getOrderSettingsInfo(),
        enabled: !!user,
        retry: false,
        refetchOnWindowFocus: false,
        onError: (error) => {
            handleUnauthorized401QueryError(error, refreshToken, orderSettingsQuery.refetch);
        }
    });

    const storefrontSettingsQuery = useQuery({
        queryKey: ['storefront-settings'],
        queryFn: () => getStorefrontSettingsInfo(),
        enabled: !!user,
        retry: false,
        refetchOnWindowFocus: false,
        onError: (error) => {
            handleUnauthorized401QueryError(error, refreshToken, storefrontSettingsQuery.refetch);
        }
    });

    const addonProductSettingsQuery = useQuery({
        queryKey: ['addon-product-settings'],
        queryFn: () => getAddonProductSettingsInfo(),
        enabled: !!user,
        retry: false,
        refetchOnWindowFocus: false,
        onError: (error) => {
            handleUnauthorized401QueryError(error, refreshToken, addonProductSettingsQuery.refetch);
        }
    });

    const productGroupSettingsQuery = useQuery({
        queryKey: ['product-group-settings'],
        queryFn: () => getProductGroupSettingsInfo(),
        enabled: !!user,
        retry: false,
        refetchOnWindowFocus: false,
        onError: (error) => {
            handleUnauthorized401QueryError(error, refreshToken, productGroupSettingsQuery.refetch);
        }
    });

    const customerSettingsQuery = useQuery({
        queryKey: ['customer-settings'],
        queryFn: () => getCustomerSettingsInfo(),
        enabled: !!user,
        retry: false,
        refetchOnWindowFocus: false,
        onError: (error) => {
            handleUnauthorized401QueryError(error, refreshToken, customerSettingsQuery.refetch);
        }
    });

    const invoiceSettingsQuery = useQuery({
        queryKey: ['invoice-settings'],
        queryFn: () => getInvoiceSettingsInfo(),
        enabled: !!user,
        retry: false,
        refetchOnWindowFocus: false,
        onError: (error) => {
            handleUnauthorized401QueryError(error, refreshToken, invoiceSettingsQuery.refetch);
        }
    });

    const productSettings = productSettingsQuery.data?.results?.[0];
    const orderSettings = orderSettingsQuery.data?.results?.[0];
    const storefrontSettings = storefrontSettingsQuery.data?.results?.[0];
    const addonProductSettings = addonProductSettingsQuery.data?.results?.[0];
    const productGroupSettings = productGroupSettingsQuery.data?.results?.[0];
    const customerSettings = customerSettingsQuery.data?.results?.[0];
    const invoiceSettings = invoiceSettingsQuery.data?.results?.[0];
    const settingsQueriesLoading = (
        productSettingsQuery.isLoading ||
        orderSettingsQuery.isLoading ||
        storefrontSettingsQuery.isLoading ||
        addonProductSettingsQuery.isLoading ||
        productGroupSettingsQuery.isLoading ||
        customerSettingsQuery.isLoading ||
        invoiceSettingsQuery.isLoading
    );
    const hasAnyReviewedSettings = [
        productSettings,
        orderSettings,
        storefrontSettings,
        addonProductSettings,
        productGroupSettings,
        customerSettings,
        invoiceSettings,
    ].some((settingsRecord) => settingsRecord?.is_reviewed === true);
    const shouldShowInitialSettingsBanner = Boolean(
        user &&
        !settingsQueriesLoading &&
        !hasAnyReviewedSettings
    );

    useQuery(
        ['state', "INDIA"],
        () => getState(1, '', '', "INDIA", '', '', 1000),
        {
            enabled: true,
            onSuccess: (data) => {
                const options = data.results.map((state) => ({
                    value: state.name, label: state.name
                }));
                setStateOptions(options);
                // setMerchantKYCIndiaModal(false)
                // setBusinessState(null);  // Clear state selection when country changes
                // setCityOptions([]);  // Clear city options when country changes
                // setBusinessCity(null);  // Clear city selection when country changes
            },
            onError: (error) => {
                handleUnauthorized401QueryError(error, refreshToken);
                if (error.response.status !== 401) {
                    toast.error(`An error occurred! ${error.response.data.detail}`);
                }
            }
        }
    );


    const addMerchantKYCIndiaMutation = useMutation({
        mutationFn: addMerchantKYCIndia,
        onSuccess: (data, variables) =>{
            queryClient.invalidateQueries(['user'])
            toggleMerchantKYCIndiaModal()
            setIdentityProofFile(null)
            setBusinessProofFile(null)
            setProfilePic("")
            setGender("MALE")
            setIdentityProofType("AADHAAR")
            setIdentityProofNumber("")
            setBusinessName("")
            setBrandName("")
            setBrandLogo("")
            setBusinessAddress1("")
            setBusinessAddress2("")
            setBusinessAddress3("")
            setBusinessState("")
            // setBusinessCountry("INDIA")
            setBusinessCity("")
            setBusinessPincode("")
            setBusinessProofType("GSTIN")
            setBusinessProofNumber("")
            setGSTIN("")
            setGSTINError("")
            setPAN("")
            setCIN("")
            setGstRegistrationType("UNREGISTERED")
            toast.success('Merchant KYC Submitted!')
        },
        // retry: true,
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, addMerchantKYCIndiaMutation, variables)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.user}`)
            }
        }
    })

    const sendOTPPhoneMutation = useMutation({
        mutationFn: sendPhoneOTP,
        onSuccess: (data, variables) =>{
            // queryClient.invalidateQueries(['user'])
            // toggleMerchantKYCIndiaModal()
            setOTPSent(true)
            toast.success('OTP Sent!')
        },
        // retry: true,
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, sendOTPPhoneMutation, variables)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
        }
    })

    const verifyOTPPhoneMutation = useMutation({
        mutationFn: verifyPhoneOTP,
        onSuccess: (data, variables) =>{
            queryClient.invalidateQueries(['user'])
            togglePhoneNumberVerifyModal()
            // toggleMerchantKYCIndiaModal()
            toast.success('Phone Number Verified!')
        },
        // retry: true,
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, verifyOTPPhoneMutation, variables)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
        }
    })

    const handleMerchantKYCIndiaFormSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        if(profilePic) formData.append("profile_picture", profilePic);
        formData.append("gender", gender);
        // formData.append("id_proof", profileIDProof);
        formData.append("identity_proof_type", identityProofType);
        formData.append("identity_proof_number", identityProofNumber);
        if(identityProofFile) formData.append("identity_proof_file", identityProofFile);
        formData.append("business_name", businessName)
        formData.append("brand_name", brandName)
        if(brandLogo) formData.append("brand_logo", brandLogo);
        formData.append("business_address1", businessAddress1);
        formData.append("business_address2", businessAddress2);
        formData.append("business_address3", businessAddress3);
        formData.append("business_country", 1);
        formData.append("business_state", businessState?.value);
        formData.append("business_city", businessCity);
        formData.append("business_pincode", businessPincode);
        formData.append("business_proof_type", businessProofType);
        formData.append("business_proof_number", businessProofNumber);
        if(identityProofFile) formData.append("business_proof_file", businessProofFile);
        formData.append("gstin", GSTIN);
        formData.append("cin", CIN);
        formData.append("pan", PAN);
        formData.append("gst_registration_type", gstRegistrationType);
        addMerchantKYCIndiaMutation.mutate(formData)
    };

    const handleSendOTPFormSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append("phone", phone);
        sendOTPPhoneMutation.mutate(formData)
    };

    const handleVerifyOTPFormSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append("phone", phone);
        formData.append("otp", OTP);
        verifyOTPPhoneMutation.mutate(formData)
    };

    return (
        <div id="wrapper">
            <Navbar />
            <SideNav />

            <div className="content-page">
                <div className="content">
                    <div className="container-fluid">
                        <div className="page-title-right">
                            <nav aria-label="breadcrumb">
                                <Breadcrumbs />
                            </nav>
                        </div>

                        <Modal className={"modal-lg"} isOpen={phoneNumberVerifyModal} toggle={togglePhoneNumberVerifyModal}>
                            <ModalHeader toggle={togglePhoneNumberVerifyModal} close={
                                <button className="close" onClick={togglePhoneNumberVerifyModal}>
                                    &times;
                                </button>
                            }>Phone Number Verify</ModalHeader>
                            <ModalBody>
                                {/*<p>Merchant KYC India</p>*/}
                                <Form onSubmit={handleSendOTPFormSubmit} encType="multipart/form-data">
                                    <FormGroup>
                                        <Label for="phone">
                                            Phone Number
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id="phone"
                                            name="text"
                                            type="text"
                                            value={phone}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setPhone(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                sendOTPPhoneMutation.isError &&
                                                sendOTPPhoneMutation.error.response.data.phone
                                            }
                                        />
                                        {sendOTPPhoneMutation.isError && sendOTPPhoneMutation.error.response.data.phone && (
                                            <FormFeedback invalid>
                                                {sendOTPPhoneMutation.error.response.data.phone}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>
                                    <Button
                                        color="#4d5bed"
                                        className="bx-btn-primary float-right"
                                        disabled={sendOTPPhoneMutation.isLoading || OTPSent || !phone}
                                    >
                                        {sendOTPPhoneMutation.isLoading? <><Loader size={'small'}/>Sending OTP...</>: `Send OTP`}
                                    </Button>
                                </Form>
                                <br/>
                                {OTPSent &&(
                                    <Form onSubmit={handleVerifyOTPFormSubmit} encType="multipart/form-data">
                                        <FormGroup>
                                            <Label for="otp">
                                                OTP
                                                <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                id="otp"
                                                name="text"
                                                type="text"
                                                // defaultValue={data.results[0].address}
                                                // disabled={isSubmitted}
                                                onChange={(e) =>
                                                    setOTP(
                                                        e.target.value
                                                    )
                                                }
                                                invalid={
                                                    verifyOTPPhoneMutation.isError &&
                                                    verifyOTPPhoneMutation.error.response.data.otp
                                                }
                                            />
                                            {verifyOTPPhoneMutation.isError && verifyOTPPhoneMutation.error.response.data.otp && (
                                                <FormFeedback invalid>
                                                    {verifyOTPPhoneMutation.error.response.data.otp}
                                                </FormFeedback>
                                            )}
                                        </FormGroup>
                                        <Button
                                            color="#4d5bed"
                                            className="bx-btn-primary float-right"
                                            disabled={verifyOTPPhoneMutation.isLoading}
                                        >
                                            {verifyOTPPhoneMutation.isLoading? <><Loader size={'small'}/>Verifying Phone...</>: `Verify Phone`}
                                        </Button>
                                    </Form>
                                )}
                            </ModalBody>
                        </Modal>

                        <Modal className={"modal-lg"} isOpen={merchantKYCIndiaModal} toggle={toggleMerchantKYCIndiaModal}>
                            <ModalHeader toggle={toggleMerchantKYCIndiaModal} close={
                                <button className="close" onClick={toggleMerchantKYCIndiaModal}>
                                    &times;
                                </button>
                            }>Merchant KYC India</ModalHeader>
                            <ModalBody>
                                {/*<p>Merchant KYC India</p>*/}
                                <Form onSubmit={handleMerchantKYCIndiaFormSubmit} encType="multipart/form-data">
                                    <FormGroup>
                                        <Label for="profile-image">Profile Picture</Label>
                                        <Input
                                            type="file"
                                            name="profile-image"
                                            id="profile-image"
                                            // disabled={isSubmitted}
                                            onChange={(e) => setProfilePic(e.target.files[0])}
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.profile_picture
                                            }
                                        />
                                        {/*<FormText color="muted">This logo will be used in all autogenerated invoices</FormText>*/}
                                        {/*<FormText color="muted">*/}
                                        {/*    {data.results[0].profile_picture}*/}
                                        {/*</FormText>*/}
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.profile_picture && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.profile_picture}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="brand_logo">Brand Logo</Label>
                                        <Input
                                            type="file"
                                            name="brand_logo"
                                            id="brand_logo"
                                            // disabled={isSubmitted}
                                            onChange={(e) => setBrandLogo(e.target.files[0])}
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.brand_logo
                                            }
                                        />
                                        <FormText color="muted">This logo will be used in all autogenerated invoices</FormText>
                                        {/*<FormText color="muted">*/}
                                        {/*    {data.results[0].profile_picture}*/}
                                        {/*</FormText>*/}
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.brand_logo && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.brand_logo}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="profile-gender">Gender<span className="text-danger">*</span></Label>
                                        <Input
                                            type="select"
                                            id="profile-gender"
                                            className={`form-control mb-2 ${addMerchantKYCIndiaMutation?.error?.response?.data?.gender ? `parsley-error`: null}`}
                                            // defaultValue={data.results[0].gender}
                                            // value={profileGender}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setGender(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            {/*<option value="--">--</option>*/}
                                            <option value={"MALE"}>
                                                Male
                                            </option>
                                            <option value={"FEMALE"}>
                                                Female
                                            </option>
                                            <option value={"OTHER"}>
                                                Other
                                            </option>
                                        </Input>
                                        <FormText color="danger">
                                            {addMerchantKYCIndiaMutation?.error?.response?.data?.gender}
                                        </FormText>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="id-proof-type">ID Proof Type<span className="text-danger">*</span></Label>
                                        <Input
                                            type="select"
                                            id="id-proof-type"
                                            className={`form-control mb-2 ${addMerchantKYCIndiaMutation?.error?.response?.data?.identity_proof_type ? `parsley-error`: null}`}
                                            // value={profileIDProofType}
                                            // defaultValue={data.results[0].id_proof_type}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setIdentityProofType(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            {/*<option value="--">--</option>*/}
                                            <option value={"AADHAAR"}>
                                                Aadhaar
                                            </option>
                                            <option value={"PAN"}>
                                                PAN
                                            </option>
                                            <option value={"VOTER_ID"}>
                                                Voter ID
                                            </option>
                                            <option value={"PASSPORT"}>
                                                Passport
                                            </option>
                                            <option value={"DRIVING_LICENSE"}>
                                                Driving License
                                            </option>

                                        </Input>
                                        <FormText color="danger">
                                            {addMerchantKYCIndiaMutation?.error?.response?.data?.identity_proof_type}
                                        </FormText>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="address">
                                            {identityProofType} Number
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id="address"
                                            name="text"
                                            type="text"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setIdentityProofNumber(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.identity_proof_number
                                            }
                                        />
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.identity_proof_number && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.identity_proof_number}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="profile-id-proof">{identityProofType} File<span className="text-danger">*</span></Label>
                                        <Input
                                            type="file"
                                            name="profile-id-proof"
                                            id="profile-id-proof"
                                            onChange={(e) => setIdentityProofFile(e.target.files[0])}
                                            // disabled={isSubmitted}
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.identity_proof_file
                                            }
                                        />
                                        {/*<FormText color="muted">*/}
                                        {/*    {data.results[0].id_proof}*/}
                                        {/*</FormText>*/}
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.identity_proof_file && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.identity_proof_file}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="business_name">
                                            Business Name
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id="business_name"
                                            name="text"
                                            type="text"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setBusinessName(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.business_name
                                            }
                                        />
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.business_name && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.business_name}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="brand_name">
                                            Brand Name
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id="brand_name"
                                            name="text"
                                            type="text"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setBrandName(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.brand_name
                                            }
                                        />
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.brand_name && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.brand_name}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="address1">
                                            Address Line1
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id="address1"
                                            name="text"
                                            type="textarea"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setBusinessAddress1(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.business_address1
                                            }
                                        />
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.business_address1 && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.business_address1}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="address2">
                                            Address Line2
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id="address2"
                                            name="text"
                                            type="textarea"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setBusinessAddress2(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.business_address2
                                            }
                                        />
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.business_address2 && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.business_address2}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="address3">
                                            Address Line3
                                            {/*<span className="text-danger">*</span>*/}
                                        </Label>
                                        <Input
                                            id="address3"
                                            name="text"
                                            type="textarea"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setBusinessAddress3(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.business_address3
                                            }
                                        />
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.business_address3 && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.business_address3}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="state">
                                            State
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Select
                                            id="business-state"
                                            value={businessState}
                                            onChange={setBusinessState}
                                            options={stateOptions}
                                            required={true}
                                            // isDisabled={isSubmitted || !businessCountry}
                                            placeholder={businessState}
                                            styles={{
                                                control: (baseStyles) => ({
                                                    ...baseStyles,
                                                    borderColor: "#495561",
                                                    backgroundColor: "#414D59",
                                                    color: "white",
                                                    padding: "0.45 0.9",
                                                    border: 'none',
                                                    boxShadow: 'none'
                                                }),
                                                menu: (baseStyles) => ({
                                                    ...baseStyles,
                                                    backgroundColor: "#414D59",
                                                    color: "#A8B5C3"
                                                }),
                                                input: (baseStyles) => ({
                                                    ...baseStyles,
                                                    color: "white"
                                                }),
                                                singleValue: (baseStyles) => ({
                                                    ...baseStyles,
                                                    color: "white"
                                                })
                                            }}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="city">
                                            City
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id="city"
                                            name="text"
                                            type="text"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setBusinessCity(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.business_city
                                            }
                                        />
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.business_city && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.business_city}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="pincode">
                                            Pincode
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id="pincode"
                                            name="text"
                                            type="text"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setBusinessPincode(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.business_pincode
                                            }
                                        />
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.business_pincode && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.business_pincode}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="business-proof-type">Business Proof Type<span className="text-danger">*</span></Label>
                                        <Input
                                            type="select"
                                            id="business-proof-type"
                                            className={`form-control mb-2 ${addMerchantKYCIndiaMutation?.error?.response?.data?.business_proof_type ? `parsley-error`: null}`}
                                            // value={profileIDProofType}
                                            // defaultValue={data.results[0].id_proof_type}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setBusinessProofType(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            {/*<option value="--">--</option>*/}
                                            <option value={"GSTIN"}>
                                                GSTIN
                                            </option>
                                            <option value={"SHOP_LICENSE"}>
                                                Shop & Establishment License
                                            </option>
                                            <option value={"UDYAM"}>
                                                Udyam Registration
                                            </option>
                                            <option value={"INCORPORATION"}>
                                                Company Incorporation Certificate
                                            </option>
                                            <option value={"TRADE_LICENSE"}>
                                                Trade License
                                            </option>
                                            {/*<option value={"UNREGISTERED_BUSINESS"}>*/}
                                            {/*    Unregistered Business*/}
                                            {/*</option>*/}
                                        </Input>
                                        <FormText color="danger">
                                            {addMerchantKYCIndiaMutation?.error?.response?.data?.business_proof_type}
                                        </FormText>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="business-proof-number">
                                            {businessProofType} Number
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id="business-proof-number"
                                            name="text"
                                            type="text"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setBusinessProofNumber(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.business_proof_number
                                            }
                                        />
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.business_proof_number && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.business_proof_number}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="profile-id-proof">{businessProofType} File<span className="text-danger">*</span></Label>
                                        <Input
                                            type="file"
                                            name="business-proof"
                                            id="business-proof"
                                            onChange={(e) => setBusinessProofFile(e.target.files[0])}
                                            // disabled={isSubmitted}
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.business_proof_file
                                            }
                                        />
                                        {/*<FormText color="muted">*/}
                                        {/*    {data.results[0].id_proof}*/}
                                        {/*</FormText>*/}
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.business_proof_file && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.business_proof_file}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="gst-registration-type">GST Registration Type</Label>
                                        <Input
                                            type="select"
                                            id="gst-registration-type"
                                            className={`form-control mb-2 ${addMerchantKYCIndiaMutation?.error?.response?.data?.gst_registration_type ? `parsley-error`: null}`}
                                            onChange={(e) => setGstRegistrationType(e.target.value)}
                                        >
                                            <option value={"UNREGISTERED"}>Unregistered</option>
                                            <option value={"REGULAR"}>Regular</option>
                                            <option value={"COMPOSITION"}>Composition</option>
                                        </Input>
                                        <FormText color="danger">
                                            {addMerchantKYCIndiaMutation?.error?.response?.data?.gst_registration_type}
                                        </FormText>
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="gstin">
                                            GSTIN
                                            {/*<span className="text-danger">*</span>*/}
                                        </Label>
                                        <Input
                                            id="gstin"
                                            name="text"
                                            type="text"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) => {
                                                const val = e.target.value.toUpperCase();
                                                setGSTIN(val);
                                                setGSTINError(val && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(val)
                                                    ? "Enter a valid GSTIN."
                                                    : "");
                                            }}
                                            invalid={
                                                !!GSTINError ||
                                                (addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.gstin)
                                            }
                                        />
                                        {GSTINError && (
                                            <FormFeedback invalid>{GSTINError}</FormFeedback>
                                        )}
                                        {!GSTINError && addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.gstin && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.gstin}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="cin">
                                            CIN
                                            {/*<span className="text-danger">*</span>*/}
                                        </Label>
                                        <Input
                                            id="cin"
                                            name="text"
                                            type="text"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setCIN(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.cin
                                            }
                                        />
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.cin && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.cin}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="pan">
                                            PAN
                                            {/*<span className="text-danger">*</span>*/}
                                        </Label>
                                        <Input
                                            id="pan"
                                            name="text"
                                            type="text"
                                            // defaultValue={data.results[0].address}
                                            // disabled={isSubmitted}
                                            onChange={(e) =>
                                                setPAN(
                                                    e.target.value
                                                )
                                            }
                                            invalid={
                                                addMerchantKYCIndiaMutation.isError &&
                                                addMerchantKYCIndiaMutation.error.response.data.pan
                                            }
                                        />
                                        {addMerchantKYCIndiaMutation.isError && addMerchantKYCIndiaMutation.error.response.data.pan && (
                                            <FormFeedback invalid>
                                                {addMerchantKYCIndiaMutation.error.response.data.pan}
                                            </FormFeedback>
                                        )}
                                    </FormGroup>


                                    <Button
                                        color="#4d5bed"
                                        className="bx-btn-primary float-right"
                                        disabled={addMerchantKYCIndiaMutation.isLoading}
                                    >
                                        {addMerchantKYCIndiaMutation.isLoading? <><Loader size={'small'}/>Submitting Merchant KYC...</>: `Submit Merchant KYC`}

                                    </Button>
                                </Form>
                            </ModalBody>
                        </Modal>

                        {user?.kyc_status === "PENDING" && (
                            <Alert color="warning" closeClassName={"close"} className={"bg-light border-warning text-warning"}>
                                KYC not complete, click to complete KYC and activate your account!
                                <Button
                                    color="#4d5bed"
                                    className="btn-warning btn-sm ml-2"
                                    onClick={toggleMerchantKYCIndiaModal}
                                >
                                    Complete KYC
                                </Button>
                            </Alert>
                        )}

                        {user?.kyc_status === "SUBMITTED" && (
                            <Alert color="warning" closeClassName={"close"} className={"bg-light border-info text-info"}>
                                KYC details Submitted, please wait while we process your documents! It may take up to 48 hours.
                            </Alert>
                        )}

                        {user?.kyc_status === "UNDER-PROCESS" && (
                            <Alert color="warning" closeClassName={"close"} className={"bg-light border-dark text-dark"}>
                                KYC Under Process, Once verified your account will be activated.
                            </Alert>
                        )}

                        {/*{user?.kyc_status === "VERIFIED" && (*/}
                        {/*    <Alert color="warning" closeClassName={"close"} className={"bg-light border-success text-success"}>*/}
                        {/*        KYC Verified! Account is Active.*/}
                        {/*    </Alert>*/}
                        {/*)}*/}

                        {user?.kyc_status === "REJECTED" && (
                            <Alert color="warning" closeClassName={"close"} className={"bg-light border-danger text-danger"}>
                                KYC Rejected! Please contact Support.
                            </Alert>
                        )}

                        {user?.is_phone_verified === false && (
                            <Alert color="warning" closeClassName={"close"} className={"bg-light border-warning text-warning"}>
                                Phone Number not verified, click to verify your phone number with OTP!
                                <Button
                                    color="#4d5bed"
                                    className="btn-warning btn-sm ml-2"
                                    onClick={togglePhoneNumberVerifyModal}
                                >
                                    Verify Phone
                                </Button>
                            </Alert>
                        )}

                        {shouldShowInitialSettingsBanner && (
                            <Alert color="warning" closeClassName={"close"} className={"bg-light border-warning text-warning"}>
                                Initial settings not complete, review and save your product and order defaults before you start selling.
                                <Button
                                    color="#4d5bed"
                                    className="btn-warning btn-sm ml-2"
                                    onClick={() => navigate('/settings')}
                                >
                                    Complete Initial Settings
                                </Button>
                            </Alert>
                        )}

                        {user?.billing_notice_level && (
                            <Alert
                                color={user.billing_notice_level === 'restricted' ? 'danger' : 'warning'}
                                closeClassName={"close"}
                                className={user.billing_notice_level === 'restricted'
                                    ? "bg-light border-danger text-danger"
                                    : "bg-light border-warning text-warning"}
                            >
                                {user.billing_notice_message || "There was an issue with your billing."}
                                <Button
                                    color="#4d5bed"
                                    className={`btn-sm ml-2 ${user.billing_notice_level === 'restricted' ? 'btn-danger' : 'btn-warning'}`}
                                    onClick={() => navigate(user.billing_notice_code === 'NO_PAYMENT_METHOD'
                                        ? '/billing/payment-method'
                                        : '/billing/billing-invoice')}
                                >
                                    {user.billing_notice_code === 'NO_PAYMENT_METHOD' ? 'Add Payment Method' : 'View Billing'}
                                </Button>
                            </Alert>
                        )}

                        <Outlet />
                    </div>
                </div>
                <footer className="footer">
                    <div className="container-fluid">
                        <div className="text-center">
                            Copyright © {currentYear} All Rights Reserved - BlackX
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default Layout;
